/** @jsxImportSource @emotion/react */

import {
  Errors,
  Input,
  ListItem,
  Select,
  Setters,
  Switch,
  Typography,
  createStyles,
} from '@quickbit/qb-design-system';
import { Config, MerchantBOLayoutMode } from 'tabs/types';

interface Props {
  formFields: Config;
  formErrors: Errors<Config>;
  formSetters: Setters<Config>;
  editable: boolean;
}

const AVAILABLE_ONRAMP_CURRENCIES = [
  { label: 'BTC', value: 'BTC' },
  { label: 'USDC', value: 'USDC' },
];

const AVAILABLE_MERCHANT_LAYOUT_MODES = [
  { label: 'Orders', value: 'ORDERS' },
  { label: 'Wallet', value: 'WALLET' },
];

export const MerchantConfig = ({
  formFields,
  formErrors,
  formSetters,
  editable,
}: Props) => {
  const styles = useStyles();

  return (
    <>
      <div>
        <Typography variant="bodyBold">QB Pay Display</Typography>
        <ListItem
          title="Hide QB Brand"
          css={[styles.listItemBorder, !editable && styles.listItemDisabled]}
          action={
            <Switch
              checked={formFields.hideQbBrand}
              onChange={(checked) => formSetters.hideQbBrand.set(checked)}
              disabled={!editable}
            />
          }
          disabled={!editable}
        />

        <ListItem
          title="Hide FIAT Amount"
          css={[styles.listItemBorder, !editable && styles.listItemDisabled]}
          action={
            <Switch
              checked={formFields.hideFiatAmount}
              onChange={(checked) => formSetters.hideFiatAmount.set(checked)}
              disabled={!editable}
            />
          }
          disabled={!editable}
        />

        <ListItem
          title="Hide customer fee"
          css={[styles.listItemBorder, !editable && styles.listItemDisabled]}
          action={
            <Switch
              checked={formFields.hideCustomerFee}
              onChange={(checked) => formSetters.hideCustomerFee.set(checked)}
              disabled={!editable}
            />
          }
          disabled={!editable}
        />
      </div>

      <div>
        <Typography variant="bodyBold">Tolerance</Typography>
        <Input
          label="Lower bound"
          type="number"
          endAdornment="%"
          value={(formFields.amountToleranceLowerBound * 100).toString()}
          disabled={!editable}
          onChange={(e) =>
            formSetters.amountToleranceLowerBound.set(
              parseFloat(e.target.value) >= 0
                ? parseFloat(e.target.value) / 100
                : 0
            )
          }
          error={!!formErrors.amountToleranceLowerBound}
          helperText={formErrors.amountToleranceLowerBound}
        />
        <Input
          label="Upper bound"
          type="number"
          endAdornment="%"
          value={(formFields.amountToleranceUpperBound * 100).toString()}
          disabled={!editable}
          onChange={(e) =>
            formSetters.amountToleranceUpperBound.set(
              parseFloat(e.target.value) >= 0
                ? parseFloat(e.target.value) / 100
                : 0
            )
          }
          error={!!formErrors.amountToleranceUpperBound}
          helperText={formErrors.amountToleranceUpperBound}
        />
      </div>

      <div>
        <Typography variant="bodyBold">Permissions</Typography>
        <ListItem
          title="Allow Pay orders"
          css={[styles.listItemBorder, !editable && styles.listItemDisabled]}
          action={
            <Switch
              checked={formFields.allowPayOrders}
              onChange={(checked) => formSetters.allowPayOrders.set(checked)}
              disabled={!editable}
            />
          }
          disabled={!editable}
        />

        <ListItem
          title="Allow Onramp flow orders"
          css={[styles.listItemBorder, !editable && styles.listItemDisabled]}
          action={
            <Switch
              checked={formFields.allowOnrampOrders}
              onChange={(checked) => formSetters.allowOnrampOrders.set(checked)}
              disabled={!editable}
            />
          }
          disabled={!editable}
        />

        <ListItem
          title="Allow Processing orders"
          css={[styles.listItemBorder, !editable && styles.listItemDisabled]}
          action={
            <Switch
              checked={formFields.allowProcessingOrders}
              onChange={(checked) =>
                formSetters.allowProcessingOrders.set(checked)
              }
              disabled={!editable}
            />
          }
          disabled={!editable}
        />
        <ListItem
          title="Allow Payment Link orders"
          css={[styles.listItemBorder, !editable && styles.listItemDisabled]}
          action={
            <Switch
              checked={formFields.allowBackofficeOrderCreation}
              onChange={(checked) =>
                formSetters.allowBackofficeOrderCreation.set(checked)
              }
              disabled={!editable}
            />
          }
          disabled={!editable}
        />
        <ListItem
          title="Allow Corporate Wallet"
          css={[styles.listItemBorder, !editable && styles.listItemDisabled]}
          action={
            <Switch
              checked={formFields.allowCorporateWallet}
              onChange={(checked) =>
                formSetters.allowCorporateWallet.set(checked)
              }
              disabled={!editable}
            />
          }
          disabled={!editable}
        />
        <ListItem
          title="Use Wallet Recipient Duality"
          css={[styles.listItemBorder, !editable && styles.listItemDisabled]}
          action={
            <Switch
              checked={formFields.useWalletRecipientDuality}
              onChange={(checked) =>
                formSetters.useWalletRecipientDuality.set(checked)
              }
              disabled={!editable}
            />
          }
          disabled={!editable}
        />
        <ListItem
          title="Allow trusted traffic"
          css={[styles.listItemBorder, !editable && styles.listItemDisabled]}
          action={
            <Switch
              checked={formFields.allowTrustedTraffic}
              onChange={(checked) =>
                formSetters.allowTrustedTraffic.set(checked)
              }
              disabled={!editable}
            />
          }
          disabled={!editable}
        />
      </div>
      <div>
        <Typography variant="bodyBold">Data access</Typography>
        <ListItem
          title="Can Access Credit Card Information"
          css={[styles.listItemBorder, !editable && styles.listItemDisabled]}
          action={
            <Switch
              checked={formFields.canAccessCreditCardInfo}
              onChange={(checked) =>
                formSetters.canAccessCreditCardInfo.set(checked)
              }
              disabled={!editable}
            />
          }
          disabled={!editable}
        />
        <ListItem
          title="Can Access Personal Information"
          css={[styles.listItemBorder, !editable && styles.listItemDisabled]}
          action={
            <Switch
              checked={formFields.canAccessPersonalInformation}
              onChange={(checked) =>
                formSetters.canAccessPersonalInformation.set(checked)
              }
              disabled={!editable}
            />
          }
          disabled={!editable}
        />
      </div>
      <div>
        <Typography variant="bodyBold">Order cancellation</Typography>
        <Input
          label="Cancel after"
          type="number"
          value={formFields.minutesToCancelOrderAfter.toString()}
          onChange={(e) => {
            if (e.target.value) {
              const value = Number(e.target.value);
              if (value < 0) return;
              formSetters.minutesToCancelOrderAfter.set(
                parseInt(e.target.value)
              );
            } else {
              formSetters.minutesToCancelOrderAfter.set(0);
            }
          }}
          endAdornment="minutes"
          error={!!formErrors.minutesToCancelOrderAfter}
          helperText={formErrors.minutesToCancelOrderAfter}
        />
        <ListItem
          title="Cancel orders on error"
          css={[styles.listItemBorder, !editable && styles.listItemDisabled]}
          action={
            <Switch
              checked={formFields.cancelOrderOnError}
              onChange={(checked) =>
                formSetters.cancelOrderOnError.set(checked)
              }
              disabled={!editable}
            />
          }
          disabled={!editable}
        />
      </div>
      <div>
        <Typography variant="bodyBold">Order callbacks</Typography>
        <ListItem
          title="Retry order callbacks"
          css={[styles.listItemBorder, !editable && styles.listItemDisabled]}
          action={
            <Switch
              checked={formFields.retryOrderCallbacks}
              onChange={(checked) =>
                formSetters.retryOrderCallbacks.set(checked)
              }
              disabled={!editable}
            />
          }
          disabled={!editable}
        />
      </div>
      <div>
        <Select
          label="Onramp currency"
          options={AVAILABLE_ONRAMP_CURRENCIES}
          values={[formFields.onrampCurrency]}
          disabled={!editable}
          onSelect={([selected]) => {
            formSetters.onrampCurrency.set(selected);
          }}
        />
      </div>
      <div>
        <Typography variant="bodyBold">Merchant B/O Layout</Typography>
        <Select
          label="Layout Mode"
          options={AVAILABLE_MERCHANT_LAYOUT_MODES}
          values={[formFields.layoutMode]}
          disabled={!editable}
          onSelect={([selected]) => {
            formSetters.layoutMode.set(selected as MerchantBOLayoutMode);
          }}
        />
      </div>
    </>
  );
};

const useStyles = createStyles(({ spacing, palette }) => ({
  listItemBorder: {
    borderRadius: spacing.xxxs,
    border: `1px solid ${palette.grey.x200}`,
    ':hover': {
      borderColor: palette.grey.x600,
    },
  },
  listItemDisabled: {
    ':hover': {
      borderColor: 'inherit',
    },
  },
}));
